import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';

const API = getApiBaseUrl();

function getRoleLabel(role) {
    if (!role) return 'участник';
    const r = role.toLowerCase();
    if (r === 'student') return 'ученик';
    if (r === 'coach') return 'тренер';
    if (r === 'parent') return 'родитель';
    return role;
}

export default function InviteConfirmPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [action, setAction] = useState(null);
    const [doneMessage, setDoneMessage] = useState('');

    useEffect(() => {
        if (!token) {
            setError('Ссылка приглашения недействительна.');
            setLoading(false);
            return;
        }

        const authToken = localStorage.getItem('token');
        if (!authToken) {
            navigate(`/login?next=${encodeURIComponent(`/invite/confirm/${token}`)}`, { replace: true });
            return;
        }

        axios.get(`${API}/invite/${token}`, {
            headers: { Authorization: `Bearer ${authToken}` }
        })
            .then((res) => {
                if (!res.data?.valid) throw new Error(res.data?.error || 'invite_not_found');
                setInvite(res.data);
            })
            .catch(() => setError('Приглашение не найдено или уже обработано.'))
            .finally(() => setLoading(false));
    }, [token, navigate]);

    const authHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    const accept = async () => {
        setAction('accepting');
        setError('');
        try {
            await axios.post(`${API}/invite/${token}/accept`, {}, authHeaders());
            setDoneMessage('Приглашение принято!');
            setAction('done');
            setTimeout(() => navigate('/'), 1500);
        } catch {
            setError('Не удалось принять приглашение.');
            setAction(null);
        }
    };

    const decline = async () => {
        setAction('declining');
        setError('');
        try {
            await axios.post(`${API}/invite/${token}/decline`, {}, authHeaders());
            setDoneMessage('Приглашение отклонено.');
            setAction('done');
            setTimeout(() => navigate('/'), 1500);
        } catch {
            setError('Не удалось отклонить приглашение.');
            setAction(null);
        }
    };

    if (loading) {
        return (
            <div className="invite-confirm-page" style={pageStyle}>
                <p style={{ color: '#666' }}>Загрузка...</p>
            </div>
        );
    }

    if (action === 'done') {
        return (
            <div className="invite-confirm-page" style={pageStyle}>
                <div className="confirm-card" style={cardStyle}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                    <h2 style={{ margin: 0 }}>{doneMessage || 'Готово!'}</h2>
                </div>
            </div>
        );
    }

    if (error && !invite) {
        return (
            <div className="invite-confirm-page" style={pageStyle}>
                <div className="confirm-card" style={cardStyle}>
                    <p style={{ color: '#dc2626' }}>{error}</p>
                </div>
            </div>
        );
    }

    const roleLabel = getRoleLabel(invite?.expectedRole);

    return (
        <div className="invite-confirm-page" style={pageStyle}>
            <div className="confirm-card" style={cardStyle}>
                <div className="confirm-logo" style={{ fontSize: 56, marginBottom: 16 }}>⚡</div>
                <h1 className="confirm-title" style={{ fontSize: 22, margin: '0 0 20px', textAlign: 'center' }}>
                    Приглашение в школу
                </h1>

                <div className="school-card" style={schoolCardStyle}>
                    <div className="school-icon" style={{ fontSize: 32 }}>🏫</div>
                    <div className="school-info">
                        <div className="school-name" style={{ fontSize: 17, fontWeight: 700 }}>
                            {invite?.schoolName}
                        </div>
                        <div className="school-role" style={{ fontSize: 13, color: '#EB3333', marginTop: 4 }}>
                            {roleLabel}
                        </div>
                    </div>
                </div>

                <p className="confirm-desc" style={{ fontSize: 14, color: '#666', textAlign: 'center', lineHeight: 1.5, marginBottom: 24 }}>
                    Вы хотите вступить в эту школу как {roleLabel}?
                </p>

                {error ? <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</p> : null}

                <button
                    type="button"
                    className="btn-accept"
                    onClick={accept}
                    disabled={!!action}
                    style={acceptBtnStyle}
                >
                    {action === 'accepting' ? '⏳ Принимаем...' : '✅ Принять приглашение'}
                </button>

                <button
                    type="button"
                    className="btn-decline"
                    onClick={decline}
                    disabled={!!action}
                    style={declineBtnStyle}
                >
                    {action === 'declining' ? '⏳ Отклоняем...' : '❌ Отклонить'}
                </button>

                <p className="confirm-hint" style={{ fontSize: 12, color: '#999', textAlign: 'center', marginTop: 16, lineHeight: 1.4 }}>
                    После принятия вы получите доступ к расписанию и журналу посещаемости.
                </p>
            </div>
        </div>
    );
}

const pageStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: '#f5f5f5',
};

const cardStyle = {
    width: '100%',
    maxWidth: 420,
    background: '#fff',
    borderRadius: 20,
    padding: 28,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    textAlign: 'center',
};

const schoolCardStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: '#FFF5F5',
    border: '1px solid #FECACA',
    borderRadius: 16,
    padding: 16,
    marginBottom: 20,
    textAlign: 'left',
};

const acceptBtnStyle = {
    width: '100%',
    background: '#EB3333',
    color: '#fff',
    border: 0,
    borderRadius: 14,
    padding: '14px 16px',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    marginBottom: 10,
};

const declineBtnStyle = {
    width: '100%',
    background: 'transparent',
    color: '#999',
    border: 0,
    borderRadius: 14,
    padding: '12px 16px',
    fontSize: 15,
    cursor: 'pointer',
};
